import { Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { today } from "../../../../common/helpers/formatDateTime";
import useTesSalaryComponents, {
  SalaryFormValues,
} from "../../../../hooks/useTesSalaryComponents";
import { ModalState } from "../../../customer/types/types";
import {
  addEmploymentSalary,
  editEmploymentSalary,
} from "../../../employment/employmentSlice";
import { SelectedSalaryRow } from "../types";
import SalaryRowForm from "./SalaryRowForm";
import SalaryTable from "./salaryRows/SalaryTable";

type Props = {
  canEdit: boolean;
};

const formatPayload = (
  data: SalaryFormValues,
  employmentId: string,
  version: number,
) => {
  const {
    components: { from, ...rest },
    tesSalaryTableSelection,
  } = data;

  if (data.components.from) {
    const payload = {
      employmentId,
      version,
      setSalary: {
        from: data.components.from,
        components: rest,
        tesSalaryTableSelection: tesSalaryTableSelection || null,
      },
    };

    return payload;
  }
};

// TODO: rename the component
const SalaryRows2 = ({ canEdit }: Props) => {
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });
  const [selectedSalaryRow, setSelectedSalaryRow] =
    useState<SelectedSalaryRow | null>(null);

  const employment = useAppSelector((state) => state.employment.additionalInfo);

  const dispatch = useAppDispatch();

  const schedulingClasses = useAppSelector(
    (state) => state.localized.schedulingClass,
  );

  const { t } = useTranslation();
  const tesId = employment.tesId;

  const memoizedDefaultValues = useMemo(() => {
    return employment?.details?.salary?.ranges.map((range) => ({
      components: {
        ...range.components,
        from: range.from,
      },
      totalSalary: "",
      tesSalaryTableSelection: range.tesSalaryTableSelection || null,
    }));
  }, [employment?.details?.salary?.ranges]);

  const { salaryComponents, salaryRangeDefaultValues } = useTesSalaryComponents(
    {
      tesId,
      validFrom: new Date(today("yyyy-MM-dd")),
      defaultRangeValues: memoizedDefaultValues,
      salaryType: employment?.details?.salaryType,
    },
  );

  const methods = useForm({
    defaultValues: { salaryRows: [...salaryRangeDefaultValues] },
    mode: "onChange",
  });

  const { control, reset } = methods;

  const { fields, prepend, update, remove, insert } = useFieldArray({
    control,
    name: "salaryRows",
  });

  useEffect(() => {
    reset({ salaryRows: [...salaryRangeDefaultValues] });
  }, [salaryRangeDefaultValues, reset]);

  useEffect(() => {
    if (modalState.id) {
      setSelectedSalaryRow({ ...fields[+modalState.id], isCopy: false });
    } else {
      setSelectedSalaryRow(null);
    }
  }, [modalState.id, fields]);

  const baseComponentCodes =
    salaryComponents?.components.baseComponents.map(
      (component) => component.code,
    ) || [];

  const personalComponentCodes =
    salaryComponents?.components.personalComponents.map(
      (component) => component.code,
    ) || [];

  const extraComponentCodes =
    salaryComponents?.components.extraComponents.map(
      (component) => component.code,
    ) || [];

  const onEdit = (index: number) => {
    setModalState({ show: true, id: index.toString() });
  };

  const onClose = () => {
    setModalState({ show: false, id: "" });
    setSelectedSalaryRow(null);
  };

  const onAdd = () => setModalState({ show: true, id: "" });

  const onAppend = (data: SalaryFormValues) => {
    const payload = formatPayload(
      data,
      employment.employmentId,
      employment.details.version,
    );

    if (payload) {
      dispatch(addEmploymentSalary({ ...payload })).then((action) => {
        if (isFulfilled(action)) {
          const currentSalaryDate = data.components.from;

          const insertIndex = fields.findIndex(
            (field) => field.components.from > currentSalaryDate,
          );

          if (insertIndex === -1) {
            prepend(data);
            return onClose();
          }

          insert(insertIndex, data);
          onClose();
        }
      });
    }
  };

  const onUpdate = (data: SalaryFormValues, index: number) => {
    onEdit(index);

    const payload = formatPayload(
      data,
      employment.employmentId,
      employment.details.version,
    );

    if (payload) {
      dispatch(editEmploymentSalary({ ...payload })).then((action) => {
        if (isFulfilled(action)) {
          update(parseInt(index.toString()), data);
          onClose();
        }
      });
    }
  };

  const onCopy = (index: number) => {
    // add with prefilled data
    onAdd();
    setSelectedSalaryRow({
      ...fields[index],
      components: { ...fields[index].components, from: "" },
      isCopy: true,
    });
  };

  const onRemove = (index: number) => {
    const field = fields[index];
    const payload = {
      employmentId: employment.employmentId,
      version: employment.details.version,
      remove: field.components.from,
    };

    dispatch(editEmploymentSalary({ ...payload })).then((action) => {
      if (isFulfilled(action)) {
        remove(index);
        onClose();
      }
    });
  };

  return (
    <>
      <FormSection title={t("talentProfile:salaryRows")}>
        <Grid
          item
          xs={2}
          container
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
        >
          <Typography variant="secondary" textAlign="end">
            {t("schedulingPeriods:schedulingPeriod")}
          </Typography>
        </Grid>
        <Grid item mb={2} xs={10}>
          <Typography>
            {schedulingClasses[employment.details?.paymentPeriodClassId]}
          </Typography>
        </Grid>
        <SalaryTable
          baseComponentCodes={baseComponentCodes}
          personalComponentCodes={personalComponentCodes}
          extraComponentCodes={extraComponentCodes}
          salaryType={employment?.details?.salaryType}
          fields={fields}
          onEdit={onEdit}
          onAdd={onAdd}
          onRemove={onRemove}
          onCopy={onCopy}
          canEdit={canEdit}
        />
      </FormSection>
      <DialogComponent open={modalState.show} onClose={onClose}>
        <FormProvider {...methods}>
          <SalaryRowForm
            tesId={tesId}
            workTitleId={employment?.details?.workTitleId ?? "none"}
            selectedSalaryRow={selectedSalaryRow}
            onClose={onClose}
            onAppend={onAppend}
            onUpdate={onUpdate}
            salaryType={employment?.details?.salaryType}
          />
        </FormProvider>
      </DialogComponent>
    </>
  );
};

export default SalaryRows2;
